/** XeAgents.ai - Apollo.io BYOK Integration Settings **/
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/lib/date-format";
import {
  Key,
  Shield,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Trash2,
  RefreshCw,
  AlertCircle,
  Sparkles,
  Linkedin,
  Clock,
  Bell,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { LeadProSettingsCard } from "./LeadProSettingsCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ApolloCredential {
  id: string;
  name: string;
  maskedApiKey: string;
  isActive: boolean;
  healthStatus: string;
  totalApiCalls: number;
  creditsRemaining: number | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApolloCredentialResponse {
  configured: boolean;
  credential?: ApolloCredential;
}

interface ApolloTestResult {
  valid: boolean;
  message: string;
  details?: string;
  account?: {
    email: string;
    name: string;
    creditsUsed: number;
    creditsRemaining: number;
  };
}

interface OrganizationResponse {
  organization: {
    id: string;
    name: string;
    country: string;
  };
  role: string;
}

export default function IntegrationsSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResult, setTestResult] = useState<ApolloTestResult | null>(null);

  // Fetch organization details to check if user is owner or admin
  const { data: orgResponse } = useQuery<OrganizationResponse>({
    queryKey: ["/api/organizations/me"],
  });

  const canManageIntegrations = orgResponse?.role === "owner" || orgResponse?.role === "admin";

  // Fetch Apollo credential status
  const { data: apolloData, isLoading } = useQuery<ApolloCredentialResponse>({
    queryKey: ["/api/organizations/apollo"],
    enabled: canManageIntegrations,
  });

  // Test API key mutation
  const testMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await apiRequest("POST", "/api/organizations/apollo/test", { apiKey: key });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to test API key");
      }
      return res.json() as Promise<ApolloTestResult>;
    },
    onSuccess: (data) => {
      setTestResult(data);
      if (data.valid) {
        toast({
          title: "API Key Valid",
          description: "Your Apollo.io API key is working correctly.",
        });
      } else {
        toast({
          title: "Invalid API Key",
          description: data.message || "The API key could not be verified.",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Test Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Save API key mutation
  const saveMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await apiRequest("POST", "/api/organizations/apollo", { apiKey: key });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to save API key");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "API Key Saved",
        description: "Your Apollo.io API key has been configured successfully.",
      });
      setTestResult(null);
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/apollo"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/apollo/reveal"] });
    },
    onError: (error: any) => {
      toast({
        title: "Save Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete API key mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/organizations/apollo");
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to delete API key");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "API Key Removed",
        description: "Your Apollo.io API key has been removed. The platform will use its global key.",
      });
      setApiKey("");
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/apollo"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/apollo/reveal"] });
    },
    onError: (error: any) => {
      toast({
        title: "Delete Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Fetch the full API key when credential is configured
  const { data: revealedKey } = useQuery<{ success: boolean; apiKey: string }>({
    queryKey: ["/api/organizations/apollo/reveal"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organizations/apollo/reveal");
      if (!res.ok) {
        throw new Error("Failed to retrieve API key");
      }
      return res.json();
    },
    enabled: canManageIntegrations && apolloData?.configured === true,
  });

  // Auto-populate the input field with existing key
  useEffect(() => {
    if (revealedKey?.apiKey && apolloData?.configured) {
      setApiKey(revealedKey.apiKey);
    }
  }, [revealedKey, apolloData?.configured]);

  const handleTest = () => {
    if (!apiKey.trim()) {
      toast({
        title: "API Key Required",
        description: "Please enter an API key to test.",
        variant: "destructive",
      });
      return;
    }
    testMutation.mutate(apiKey.trim());
  };

  const handleSave = () => {
    if (!apiKey.trim()) {
      toast({
        title: "API Key Required",
        description: "Please enter an API key to save.",
        variant: "destructive",
      });
      return;
    }
    saveMutation.mutate(apiKey.trim());
  };

  if (!canManageIntegrations) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground mt-1">
            Manage third-party integrations for your organization
          </p>
        </div>

        <Alert>
          <Shield className="h-4 w-4" />
          <AlertTitle>Admin Access Required</AlertTitle>
          <AlertDescription>
            Only organization owners and admins can manage integrations and API keys. Please contact your organization administrator for access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground mt-1">
          Manage third-party integrations for your organization
        </p>
      </div>

      {/* Apollo.io Integration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Apollo.io
                  {apolloData?.configured && (
                    <Badge variant="default" className="ml-2">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Lead generation and contact enrichment for Campaign AI
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href="https://www.apollo.io/api" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Get API Key
              </a>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current Status */}
          {apolloData?.configured && apolloData.credential && (
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    Current API Key
                  </h4>
                  <div className="flex items-center gap-2">
                    <Badge variant={apolloData.credential.healthStatus === "healthy" ? "default" : "secondary"}>
                      {apolloData.credential.healthStatus}
                    </Badge>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove Apollo.io API Key?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove your organization's Apollo.io API key. Campaign AI will fall back to using the platform's global API key.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteMutation.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {deleteMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : null}
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">API Key</p>
                    <p className="font-mono">{apolloData.credential.maskedApiKey}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total API Calls</p>
                    <p className="font-semibold">{apolloData.credential.totalApiCalls.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Credits Remaining</p>
                    <p className="font-semibold">
                      {apolloData.credential.creditsRemaining !== null
                        ? apolloData.credential.creditsRemaining.toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Used</p>
                    <p className="font-semibold">
                      {apolloData.credential.lastUsedAt
                        ? formatDate(apolloData.credential.lastUsedAt, 'MMM d, yyyy')
                        : "Never"}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />
              <h4 className="font-medium">Update API Key</h4>
            </div>
          )}

          {/* API Key Input */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="apollo-api-key">Apollo.io API Key</Label>
              <div className="relative">
                <Input
                  id="apollo-api-key"
                  type={showApiKey ? "text" : "password"}
                  placeholder="Enter your Apollo.io API key"
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setTestResult(null);
                  }}
                  className="pr-10 font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Using your own API key gives you full control over your Apollo.io usage and credits.
              </p>
            </div>

            {/* Test Result */}
            {testResult && (
              <Alert variant={testResult.valid ? "default" : "destructive"}>
                {testResult.valid ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <AlertTitle>{testResult.message}</AlertTitle>
                {testResult.account && (
                  <AlertDescription>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div>Account: {testResult.account.name}</div>
                      <div>Email: {testResult.account.email}</div>
                      <div>Credits Used: {testResult.account.creditsUsed?.toLocaleString()}</div>
                      <div>Credits Remaining: {testResult.account.creditsRemaining?.toLocaleString()}</div>
                    </div>
                  </AlertDescription>
                )}
                {testResult.details && !testResult.valid && (
                  <AlertDescription>{testResult.details}</AlertDescription>
                )}
              </Alert>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex justify-between border-t pt-6">
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={!apiKey.trim() || testMutation.isPending}
          >
            {testMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Test Connection
          </Button>
          <Button
            onClick={handleSave}
            disabled={!apiKey.trim() || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Key className="h-4 w-4 mr-2" />
            )}
            {apolloData?.configured ? "Update API Key" : "Save API Key"}
          </Button>
        </CardFooter>
      </Card>

      {/* LinkedIn Sales Navigator Card - Coming Soon */}
      <Card className="border-dashed opacity-80">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                <Linkedin className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  LinkedIn Sales Navigator
                  <Badge variant="secondary" className="ml-2">
                    <Clock className="h-3 w-3 mr-1" />
                    Coming Soon
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Access LinkedIn's professional network for B2B lead generation
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="p-4 border rounded-lg bg-muted/30">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Get Notified When Available
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              LinkedIn Sales Navigator integration requires OAuth authentication and is currently in development.
              We'll notify you when this integration becomes available.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">OAuth 2.0 Authentication</Badge>
              <Badge variant="outline">Lead Search</Badge>
              <Badge variant="outline">Contact Export</Badge>
              <Badge variant="outline">InMail Integration</Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-foreground mb-2">Planned Features</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Search leads by title, company, industry</li>
                <li>Import contacts directly to campaigns</li>
                <li>Sync with your LinkedIn saved lists</li>
                <li>Access to company insights</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">Requirements</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>LinkedIn Sales Navigator subscription</li>
                <li>OAuth authorization (one-time setup)</li>
                <li>API access enabled on your account</li>
              </ul>
            </div>
          </div>
        </CardContent>

        <CardFooter className="border-t pt-6">
          <Button variant="outline" className="w-full" disabled>
            <Bell className="h-4 w-4 mr-2" />
            Notify Me When Available
          </Button>
        </CardFooter>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
            About BYOK (Bring Your Own Key)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>
            <strong>What is BYOK?</strong> Bring Your Own Key allows you to use your own API keys
            instead of the platform's shared keys. This gives you direct control over your API usage,
            credits, and rate limits.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-foreground mb-2">Benefits</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Full control over your API credits</li>
                <li>Higher rate limits with your own account</li>
                <li>Better visibility into API usage</li>
                <li>Access to your own saved lists and data</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">How It Works</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Your API keys are encrypted and stored securely</li>
                <li>Campaign AI will use your keys for all searches</li>
                <li>If not configured, the platform's global keys are used</li>
                <li>You can remove your keys at any time</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <LeadProSettingsCard />
      <SmtpSettingsCard />
      <CalComSettingsCard />
    </div>
  );
}

/**
 * SES submission endpoints are region-specific and the region cannot be derived
 * from the sender's domain, so discovery deliberately stops short of guessing one
 * — this picker is how an org supplies it.
 */
const SES_REGIONS: Array<{ value: string; label: string }> = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "me-south-1", label: "Middle East (Bahrain)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

interface SmtpSettings {
  configured: boolean;
  host: string;
  port: number;
  username: string;
  fromEmail: string;
  fromName: string;
  hasPassword: boolean;
}

/**
 * Outbound mail settings, scoped to this organisation.
 *
 * Mail was previously sent from one platform-wide account, so every tenant's mail
 * left from the same address and shared a sending reputation. The stored password
 * is never returned to the browser.
 */
function SmtpSettingsCard() {
  const { toast } = useToast();
  const [form, setForm] = useState({ host: "", port: "587", username: "", fromEmail: "", fromName: "" });
  const [password, setPassword] = useState("");
  const [discovery, setDiscovery] = useState<{ provider?: string; note?: string; confidence?: string } | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [sesRegion, setSesRegion] = useState("");
  // Which of the two setup routes is on screen. Derived from the saved host on
  // load rather than persisted, since an SES host is unambiguous and a stored
  // mode could otherwise drift out of step with the settings it describes.
  const [mode, setMode] = useState<"ses" | "custom">("custom");

  // Selecting a region is enough to fill in SES entirely — the host is purely a
  // function of it, and the port never varies.
  const applySesRegion = (region: string) => {
    setSesRegion(region);
    setForm((prev) => ({
      ...prev,
      host: `email-smtp.${region}.amazonaws.com`,
      port: "587",
      username: prev.username || "",
    }));
    setDiscovery({
      provider: "Amazon SES",
      note:
        "Use SES SMTP credentials, not an IAM access key — they look similar but are generated separately under SES → SMTP settings → Create SMTP credentials. The From address must also be a verified identity in this region, and if the account is still in the SES sandbox it can only send to verified recipients.",
      confidence: "high",
    });
    setTestResult(null);
  };

  const { data, isLoading } = useQuery<SmtpSettings>({
    queryKey: ["/api/organizations/smtp"],
  });

  useEffect(() => {
    if (data) {
      setForm({
        host: data.host || "",
        port: String(data.port ?? 587),
        username: data.username || "",
        fromEmail: data.fromEmail || "",
        fromName: data.fromName || "",
      });
      const sesMatch = /^email-smtp\.([a-z0-9-]+)\.amazonaws\.com$/i.exec(data.host || "");
      if (sesMatch) {
        setMode("ses");
        setSesRegion(sesMatch[1]);
      }
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await apiRequest("PUT", "/api/organizations/smtp", body);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Mail settings saved", description: "Your organisation's email will send from this account." });
      setPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/smtp"] });
    },
    onError: (error: any) => {
      toast({ title: "Couldn't save", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  const handleSave = () => {
    const body: Record<string, unknown> = { ...form, port: Number(form.port) };
    // Only send the password when one was typed, so saving other fields does not
    // wipe the stored credential.
    if (password.trim()) body.password = password.trim();
    saveMutation.mutate(body);
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  // Look the settings up from the address they just typed, so the admin does not
  // have to know their provider's submission host.
  const discoverMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await apiRequest("POST", "/api/organizations/smtp/discover", { email });
      return res.json();
    },
    onSuccess: (result: any) => {
      if (!result?.found) {
        toast({
          title: "Couldn't detect settings",
          description: result?.message || "Please enter the host and port manually.",
        });
        return;
      }
      setForm((prev) => ({
        ...prev,
        host: result.host || prev.host,
        port: String(result.port ?? prev.port),
        // Almost every provider authenticates with the full address.
        username: prev.username || prev.fromEmail,
      }));
      setDiscovery({ provider: result.provider, note: result.note, confidence: result.confidence });
      toast({
        title: result.provider ? `Detected ${result.provider}` : "Settings detected",
        description: result.host
          ? `Using ${result.host}:${result.port}.`
          : "We identified the provider but need the host from you — see the note below.",
      });
    },
    onError: (error: any) => {
      toast({ title: "Lookup failed", description: error?.message || "Please enter the settings manually.", variant: "destructive" });
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/organizations/smtp/test", {
        host: form.host,
        port: Number(form.port),
        username: form.username,
        ...(password.trim() ? { password: password.trim() } : {}),
      });
      return res.json();
    },
    onSuccess: (result: any) => {
      setTestResult(result);
      toast({
        title: result?.success ? "Connection successful" : "Connection failed",
        description: result?.message,
        variant: result?.success ? undefined : "destructive",
      });
    },
    onError: (error: any) => {
      const message = error?.message || "Could not reach the server.";
      setTestResult({ success: false, message });
      toast({ title: "Connection failed", description: message, variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Email Sending (SMTP)
          {!isLoading && (
            <Badge variant={data?.configured ? "default" : "outline"}>
              {data?.configured ? "Connected" : "Not configured"}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Send your organisation's email — document deliveries, confirmations and invites — from your
          own mail account. If not configured, the platform's default account is used.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* The two routes need different things from the admin: SES needs a region
            and nothing else, everything else needs a host we try to work out for
            them. Asking up front avoids showing both sets of controls at once. */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === "ses" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("ses")}
          >
            Amazon SES
          </Button>
          <Button
            type="button"
            variant={mode === "custom" ? "default" : "outline"}
            size="sm"
            onClick={() => { setMode("custom"); setDiscovery(null); }}
          >
            Other SMTP provider
          </Button>
        </div>

        {/* 1 — the address they send from, which is also what discovery works off */}
        <div className="space-y-2">
          <Label htmlFor="smtp-from-email">1. Email address you'll send from</Label>
          <div className="flex gap-2">
            <Input
              id="smtp-from-email"
              placeholder="no-reply@yourcompany.com"
              value={form.fromEmail}
              onChange={set("fromEmail")}
            />
            {mode === "custom" && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => discoverMutation.mutate(form.fromEmail)}
                disabled={!form.fromEmail.trim() || discoverMutation.isPending}
              >
                {discoverMutation.isPending ? "Detecting..." : "Auto-detect"}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {mode === "ses"
              ? "Must be a verified identity in your SES account, in the region below."
              : "Must be a real mailbox your provider lets you send from, or messages will be rejected."}
          </p>
        </div>

        {/* 2 — filled in by discovery, still editable */}
        <div className="space-y-2">
          <Label>2. Server settings</Label>

          {/* SES is the one common provider auto-detect cannot finish on its own:
              the submission host encodes a region that nothing in the sender's
              domain reveals. Picking it here fills the rest in. */}
          {mode === "ses" && (
            <div className="rounded-md border bg-muted/40 p-3 space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium">SES region</p>
                  <p className="text-xs text-muted-foreground">
                    Pick your region and we'll fill in the host and port.
                  </p>
                </div>
                <div className="min-w-[230px]">
                  <Select value={sesRegion} onValueChange={applySesRegion}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select SES region" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {SES_REGIONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label} — {r.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-1">
              <Input placeholder="smtp.yourprovider.com" value={form.host} onChange={set("host")} />
              <p className="text-xs text-muted-foreground">Host</p>
            </div>
            <div className="space-y-1">
              <Input inputMode="numeric" placeholder="587" value={form.port} onChange={set("port")} />
              <p className="text-xs text-muted-foreground">Port — 587 STARTTLS, 465 SSL</p>
            </div>
          </div>
          {discovery?.note && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{discovery.provider || "Before you continue"}</AlertTitle>
              <AlertDescription>{discovery.note}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* 3 — credentials */}
        <div className="space-y-2">
          <Label>3. Sign in</Label>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Input placeholder="Username — usually your full email" autoComplete="off" value={form.username} onChange={set("username")} />
              <p className="text-xs text-muted-foreground">Username</p>
            </div>
            <div className="space-y-1">
              <Input
                type="password"
                autoComplete="new-password"
                placeholder={data?.hasPassword ? "Saved — type a new one to replace it" : "Password or app password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Password — stored encrypted, never shown again</p>
            </div>
          </div>
        </div>

        {/* 4 — prove it works before it is relied on by a live call */}
        <div className="space-y-2">
          <Label>4. Test the connection</Label>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending || !form.host || !form.username || (!password.trim() && !data?.hasPassword)}
            >
              {testMutation.isPending ? "Testing..." : "Test Connection"}
            </Button>
            {testResult && (
              <span className={`text-sm ${testResult.success ? "text-green-600" : "text-destructive"}`}>
                {testResult.success ? "✓ Connected" : "✕ Failed"}
              </span>
            )}
          </div>
          {testResult && !testResult.success && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Couldn't connect</AlertTitle>
              <AlertDescription className="break-words">{testResult.message}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* 5 — display name, then save */}
        <div className="space-y-2">
          <Label htmlFor="smtp-from-name">5. From name</Label>
          <Input id="smtp-from-name" placeholder="Your Company" value={form.fromName} onChange={set("fromName")} />
          <p className="text-xs text-muted-foreground">What recipients see as the sender.</p>
        </div>
      </CardContent>
      <CardFooter className="gap-2">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? "Saving..." : "Save"}
        </Button>
        {data?.configured && (
          <Button
            variant="outline"
            onClick={() => saveMutation.mutate({ password: "" })}
            disabled={saveMutation.isPending}
          >
            Remove settings
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

interface CalComSettings {
  configured: boolean;
  username: string;
  eventSlug: string;
}

/**
 * Cal.com booking credentials, scoped to this organisation.
 *
 * Cal.com was previously configurable only through platform-wide global settings,
 * so one key served every tenant and org admins had no way to use their own. The
 * saved key is never sent back to the browser — the API reports only whether one
 * is set — so it cannot be read back out of the UI.
 */
function CalComSettingsCard() {
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("");
  const [username, setUsername] = useState("");
  const [eventSlug, setEventSlug] = useState("");

  const { data, isLoading } = useQuery<CalComSettings>({
    queryKey: ["/api/organizations/calcom"],
  });

  useEffect(() => {
    if (data) {
      setUsername(data.username || "");
      setEventSlug(data.eventSlug || "");
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (body: { apiKey?: string; username: string; eventSlug: string }) => {
      const res = await apiRequest("PUT", "/api/organizations/calcom", body);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Booking settings saved", description: "Your agents will use this Cal.com account." });
      setApiKey("");
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/calcom"] });
    },
    onError: (error: any) => {
      toast({ title: "Couldn't save", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/organizations/calcom", { apiKey: "" });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "API key removed", description: "Bookings will fall back to the platform default." });
      setApiKey("");
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/calcom"] });
    },
  });

  const handleSave = () => {
    // Omit apiKey entirely when the field is blank so saving username/slug alone
    // does not wipe the stored key.
    saveMutation.mutate(apiKey.trim() ? { apiKey: apiKey.trim(), username, eventSlug } : { username, eventSlug });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Cal.com Booking
          {!isLoading && (
            <Badge variant={data?.configured ? "default" : "outline"}>
              {data?.configured ? "Connected" : "Not configured"}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Connect your organisation's Cal.com account so agents can check real availability and book
          meetings. Without it, agents fall back to the platform default.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="calcom-api-key">API Key</Label>
          <Input
            id="calcom-api-key"
            type="password"
            autoComplete="off"
            placeholder={data?.configured ? "Saved — enter a new key to replace it" : "cal_live_..."}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Found in Cal.com under Settings &rarr; Developer &rarr; API keys. Stored encrypted and
            never shown again after saving.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="calcom-username">Cal.com Username</Label>
            <Input
              id="calcom-username"
              placeholder="Optional — uses the default if blank"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="calcom-event-slug">Event Type Slug</Label>
            <Input
              id="calcom-event-slug"
              placeholder="Optional — e.g. 30min"
              value={eventSlug}
              onChange={(e) => setEventSlug(e.target.value)}
            />
          </div>
        </div>

        {data?.configured && (
          <>
            <Separator />
            <CalComHostsSection />
          </>
        )}
      </CardContent>
      <CardFooter className="gap-2">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? "Saving..." : "Save"}
        </Button>
        {data?.configured && (
          <Button variant="outline" onClick={() => removeMutation.mutate()} disabled={removeMutation.isPending}>
            Remove key
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

interface CalComHost {
  id: string;
  calcomEventTypeId: number;
  eventTypeSlug: string;
  eventTypeTitle: string;
  hostName: string | null;
  hostEmail: string | null;
  isEnabled: boolean;
}

/**
 * Lets an org admin pick which of this Cal.com account's event types show up as
 * selectable "hosts" on an outbound agent's appointment node, and label each with
 * a display name/email (Cal.com's API doesn't reliably expose a human host
 * identity for a plain personal account).
 */
function CalComHostsSection() {
  const { toast } = useToast();
  const [drafts, setDrafts] = useState<Record<string, { hostName: string; hostEmail: string }>>({});

  const { data, isLoading } = useQuery<{ hosts: CalComHost[] }>({
    queryKey: ["/api/organizations/calcom/hosts"],
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/organizations/calcom/hosts/sync");
      return res.json();
    },
    onSuccess: (result) => {
      toast({ title: "Hosts synced", description: `Found ${result.hosts?.length ?? 0} event type(s) from Cal.com.` });
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/calcom/hosts"] });
    },
    onError: (error: any) => {
      toast({ title: "Sync failed", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; hostName?: string; hostEmail?: string; isEnabled?: boolean }) => {
      const res = await apiRequest("PATCH", `/api/organizations/calcom/hosts/${id}`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/calcom/hosts"] });
    },
    onError: (error: any) => {
      toast({ title: "Couldn't update host", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  const hosts = data?.hosts ?? [];

  const draftFor = (host: CalComHost) => drafts[host.id] ?? { hostName: host.hostName ?? "", hostEmail: host.hostEmail ?? "" };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Hosts</Label>
          <p className="text-xs text-muted-foreground">
            Each Cal.com event type can be offered as a selectable host on an outbound agent's
            appointment step. Sync to pull in new event types.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
          {syncMutation.isPending ? "Syncing..." : "Sync from Cal.com"}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading hosts…</p>
      ) : hosts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No hosts yet — click "Sync from Cal.com" to pull in this account's event types.
        </p>
      ) : (
        <div className="space-y-2">
          {hosts.map((host) => {
            const draft = draftFor(host);
            return (
              <div key={host.id} className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{host.eventTypeTitle}</p>
                  <p className="text-xs text-muted-foreground truncate">{host.eventTypeSlug}</p>
                </div>
                <Input
                  className="md:w-40"
                  placeholder="Host name"
                  value={draft.hostName}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [host.id]: { ...draft, hostName: e.target.value } }))}
                  onBlur={() => {
                    if (draft.hostName !== (host.hostName ?? "")) {
                      updateMutation.mutate({ id: host.id, hostName: draft.hostName });
                    }
                  }}
                />
                <Input
                  className="md:w-56"
                  type="email"
                  placeholder="Host email"
                  value={draft.hostEmail}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [host.id]: { ...draft, hostEmail: e.target.value } }))}
                  onBlur={() => {
                    if (draft.hostEmail !== (host.hostEmail ?? "")) {
                      updateMutation.mutate({ id: host.id, hostEmail: draft.hostEmail });
                    }
                  }}
                />
                <div className="flex items-center gap-2">
                  <Switch
                    checked={host.isEnabled}
                    onCheckedChange={(checked) => updateMutation.mutate({ id: host.id, isEnabled: checked })}
                  />
                  <span className="text-xs text-muted-foreground">{host.isEnabled ? "Enabled" : "Disabled"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
